/**
 * AddReminderModal — POST /api/calendar/events form (manual events).
 *
 * Uses the existing shadcn `Dialog` primitive so the modal shares the
 * app's global escape / focus-trap behaviour. Emits a validated
 * payload to the parent via `onCreated` and never posts twice on
 * double-click (submit button disables during flight).
 */
import { useState } from "react";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { api, formatApiError } from "@/api/client";

const REMINDER_OPTIONS = [
  { value: 0, label: "On the day" },
  { value: 1, label: "1 day before" },
  { value: 3, label: "3 days before" },
  { value: 7, label: "1 week before" },
  { value: 14, label: "2 weeks before" },
  { value: 30, label: "1 month before" },
];

export default function AddReminderModal({ open, onOpenChange, onCreated }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [eventDate, setEventDate] = useState(defaultDateInput());
  const [reminderDays, setReminderDays] = useState(7);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  function defaultDateInput() {
    // 7 days from today at 09:00 local — a friendly default for a
    // "next-week reminder" without forcing the user to type.
    const d = new Date();
    d.setDate(d.getDate() + 7);
    d.setHours(9, 0, 0, 0);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  function reset() {
    setTitle("");
    setDescription("");
    setEventDate(defaultDateInput());
    setReminderDays(7);
    setSubmitting(false);
    setError(null);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!title.trim() || !eventDate) return;
    setSubmitting(true);
    setError(null);
    try {
      const iso = new Date(eventDate).toISOString();
      const res = await api.post("/calendar/events", {
        title: title.trim(),
        description: description.trim() || null,
        event_date: iso,
        reminder_days_before: Number(reminderDays),
      });
      onCreated?.(res.data);
      reset();
      onOpenChange?.(false);
    } catch (err) {
      setError(formatApiError(err));
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange?.(next);
      }}
    >
      <DialogContent
        data-testid="calendar-add-reminder-modal"
        className="sm:max-w-md"
      >
        <DialogHeader>
          <DialogTitle data-testid="calendar-add-reminder-title">
            Add reminder
          </DialogTitle>
          <DialogDescription>
            Create a personal reminder. This appears in your calendar list and
            month view.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="reminder-title"
              className="text-[11px] uppercase tracking-[0.16em] text-v2-textMuted"
            >
              Title
            </label>
            <input
              id="reminder-title"
              data-testid="calendar-add-reminder-title-input"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              maxLength={200}
              placeholder="Follow up with mentor on grant draft"
              className="mt-1 w-full rounded-lg px-3 py-2 text-sm text-v2-text focus:outline-none focus:ring-2 focus:ring-v2-accent"
              style={{
                background: "var(--v2-surface)",
                border: "1px solid var(--v2-border)",
              }}
            />
          </div>
          <div>
            <label
              htmlFor="reminder-description"
              className="text-[11px] uppercase tracking-[0.16em] text-v2-textMuted"
            >
              Notes (optional)
            </label>
            <textarea
              id="reminder-description"
              data-testid="calendar-add-reminder-description-input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={1000}
              rows={3}
              placeholder="Anything you want to remember about this event."
              className="mt-1 w-full rounded-lg px-3 py-2 text-sm text-v2-text focus:outline-none focus:ring-2 focus:ring-v2-accent resize-none"
              style={{
                background: "var(--v2-surface)",
                border: "1px solid var(--v2-border)",
              }}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label
                htmlFor="reminder-date"
                className="text-[11px] uppercase tracking-[0.16em] text-v2-textMuted"
              >
                Date + time
              </label>
              <input
                id="reminder-date"
                data-testid="calendar-add-reminder-date-input"
                type="datetime-local"
                value={eventDate}
                onChange={(e) => setEventDate(e.target.value)}
                required
                className="mt-1 w-full rounded-lg px-3 py-2 text-sm text-v2-text focus:outline-none focus:ring-2 focus:ring-v2-accent"
                style={{
                  background: "var(--v2-surface)",
                  border: "1px solid var(--v2-border)",
                }}
              />
            </div>
            <div>
              <label
                htmlFor="reminder-lead"
                className="text-[11px] uppercase tracking-[0.16em] text-v2-textMuted"
              >
                Remind me
              </label>
              <select
                id="reminder-lead"
                data-testid="calendar-add-reminder-lead-select"
                value={reminderDays}
                onChange={(e) => setReminderDays(e.target.value)}
                className="mt-1 w-full rounded-lg px-3 py-2 text-sm text-v2-text focus:outline-none focus:ring-2 focus:ring-v2-accent"
                style={{
                  background: "var(--v2-surface)",
                  border: "1px solid var(--v2-border)",
                }}
              >
                {REMINDER_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {error && (
            <p
              data-testid="calendar-add-reminder-error"
              role="alert"
              className="text-xs text-red-400 leading-relaxed"
            >
              {error}
            </p>
          )}
          <DialogFooter>
            <button
              type="button"
              onClick={() => onOpenChange?.(false)}
              data-testid="calendar-add-reminder-cancel"
              className="text-sm text-v2-textMuted hover:text-v2-text px-4 py-2"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !title.trim() || !eventDate}
              data-testid="calendar-add-reminder-submit"
              className="inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                background: "rgba(167,139,250,0.9)",
              }}
            >
              {submitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              Add reminder
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
